"use client"

import * as React from "react"
import { ChevronDownIcon } from "lucide-react"

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import {
  ReconciliationLineState,
  type ReconciliationLine,
} from "@/features/finance/model/finance-close-trust-types"

import { ReconciliationTransitionDialog } from "./reconciliation-transition-dialog"

const STATE_VARIANT: Record<ReconciliationLineState, string> = {
  [ReconciliationLineState.Outstanding]: "outline",
  [ReconciliationLineState.Cleared]: "success",
  // Amber, not red: excluding a line is a legitimate decision, not a failure.
  [ReconciliationLineState.Excluded]: "secondary",
  [ReconciliationLineState.Investigating]: "warning",
}

/** The two verdicts a preparer chooses deliberately; clearing is the checkbox, not a menu item. */
const VERDICTS: ReconciliationLineState[] = [
  ReconciliationLineState.Excluded,
  ReconciliationLineState.Investigating,
]

/**
 * A line's verdict, and the way to change it.
 *
 * Clearing stays on the checkbox — it is the overwhelmingly common action and should cost one click. The two
 * deliberate verdicts live behind a menu because each requires a written reason: an unexplained exclusion is
 * indistinguishable from hiding an inconvenient transaction, so the API refuses one and this collects it.
 */
/**
 * Whether a line write is in flight.
 *
 * Delivered by CONTEXT rather than a prop, deliberately. This cell is rendered from a `columnDef.cell`, and a
 * prop that changed on every mutation would have to sit in the columns memo — which rebuilds the column
 * definitions, hands TanStack a new component type for every cell, and remounts the table on each keystroke.
 * A context update re-renders the consumers and leaves the column identities alone.
 */
export const ReconciliationLinePendingContext = React.createContext(false)

export function ReconciliationLineStateCell({
  line,
  editable,
  onChange,
}: {
  line: ReconciliationLine
  editable: boolean
  onChange: (lineId: string, state: ReconciliationLineState, note?: string) => void | Promise<void>
}) {
  const pending = React.useContext(ReconciliationLinePendingContext)
  const t = useTranslations("finance.reconciliation")
  const [verdict, setVerdict] = React.useState<ReconciliationLineState | null>(null)

  const badge = (
    <span className='inline-flex items-center gap-1.5'>
      <Badge variant={STATE_VARIANT[line.state] as never}>{t(`lineState.${line.state}` as never)}</Badge>
      {/* The reason is the substance of a verdict, so it is shown, not hidden behind a hover. */}
      {line.stateNote ? (
        <span className='text-muted-foreground max-w-[12rem] truncate text-xs' title={line.stateNote}>
          {line.stateNote}
        </span>
      ) : null}
    </span>
  )

  if (!editable) return badge

  return (
    <span className='inline-flex items-center gap-1'>
      {badge}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon-sm'
            disabled={pending}
            aria-label={t("lineState.change")}
            title={t("lineState.change")}
          >
            <ChevronDownIcon className='size-3.5' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {line.state !== ReconciliationLineState.Outstanding ? (
            <DropdownMenuItem onSelect={() => void onChange(line.id, ReconciliationLineState.Outstanding)}>
              {t(`lineState.${ReconciliationLineState.Outstanding}` as never)}
            </DropdownMenuItem>
          ) : null}
          {VERDICTS.filter((state) => state !== line.state).map((state) => (
            <DropdownMenuItem key={state} onSelect={() => setVerdict(state)}>
              {t(`lineState.${state}` as never)}…
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {verdict ? (
        <ReconciliationTransitionDialog
          open
          onOpenChange={(open) => {
            if (!open) setVerdict(null)
          }}
          title={t(`lineState.dialog.${verdict}.title` as never)}
          description={t(`lineState.dialog.${verdict}.body` as never)}
          confirmLabel={t(`lineState.dialog.${verdict}.confirm` as never)}
          noteLabel={t("lineState.dialog.reason")}
          notePlaceholder={t(`lineState.dialog.${verdict}.placeholder` as never)}
          noteRequired
          destructive={verdict === ReconciliationLineState.Excluded}
          pending={pending}
          onConfirm={(note) => {
            void onChange(line.id, verdict, note)
            setVerdict(null)
          }}
        />
      ) : null}
    </span>
  )
}
